import axios from '@/lib/axios'
import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { useCompanyStore } from '@/stores/company'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { get, objectOmit } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { MaybeRef, computed, watch } from 'vue'
import { ToastInterface, useToast } from 'vue-toastification'

import type { Company } from '../interfaces/company'

interface Config {
	immediate?: boolean
	errorHandling?: boolean
}

interface Params extends Omit<Partial<Company>, 'id'> {
	companyId: MaybeRef<number | string | undefined>
}

interface createCompany {
	name: string
	businessActivityId: string
	corporationId?: string
}

type UpdateParams = Partial<Params> & {
	companyId: number
	paymentMethods?: { options: string[] }
}

type CreateParams = Pick<
	Company,
	'name' | 'businessActivityId' | 'corporationId' | 'executiveId'
>

const getCompany = async (params?: Params) => {
	if (!params) return

	const { data } = await axios.get<Company>(
		`/api/companies/${get(params.companyId)}`
	)

	return data
}

export const createCompany = async (
	params: CreateParams,
	toast?: ToastInterface
) => {
	try {
		const { data } = await axios.post('/api/companies', params)

		toast?.success('La empresa se ha creado correctamente')
		return data
	} catch (err) {
		toast?.error('Error al crear la empresa')
		console.log(err)
	}
}

export const updateCompany = async (
	params: UpdateParams,
	toast?: ToastInterface
) => {
	try {
		const { companyId } = params

		const { data } = await axios.put<Company>(`/api/companies/${companyId}`, {
			...objectOmit(params, ['companyId']),
		})

		toast?.success('La empresa se ha actualizado correctamente')
		return data
	} catch (err) {
		toast?.error('Error al actualizar la empresa')
		console.log(err)
	}
}

const useCompany = (params?: Params, config?: Config) => {
	const { immediate = true } = config || {}
	const { setCompany } = useCompanyStore()
	const toast = useToast()
	const { company, id } = storeToRefs(useCompanyStore())

	/**
	 * El id resuelto, reactivo: `companyId` es un `MaybeRef` y puede llegar tarde
	 * —el llamador lo saca de `user`, que se resuelve por query—. Antes se evaluaba
	 * una sola vez, así que quien montaba antes que el usuario se quedaba con
	 * `enabled` en `false` para siempre: un booleano suelto no vuelve a mirarse.
	 *
	 * Sin fallback al `id` del store: nadie lo escribe con un valor (el único
	 * `setId` con dato es el de OpenPay, otro store) y el día que alguien lo
	 * cableara, el menú de un usuario interno —`company_id` siempre nulo— pasaría
	 * a decidirse con la empresa del cliente que tuviera abierta.
	 */
	const resolvedId = computed(() => get(params?.companyId))

	const {
		data,
		isError,
		isLoading,
		isInitialLoading,
		refetch,
		isSuccess,
		isFetching,
		isRefetching,
	} = useQuery({
		// Forma de objeto —también sirve la posicional con el ref DENTRO del arreglo,
		// que `parseQueryArgs` desenvuelve—; se usa ésta porque es la que acepta la
		// llave como `computed` y es la que ya usa `useCreditRequests`.
		queryKey: computed(() => ['company', resolvedId.value]),
		queryFn: () =>
			getCompany({
				companyId: resolvedId.value,
			}),
		// Sin el id no hay nada que pedir: sin esta guarda se pegaba a
		// `/api/companies/undefined`. Ojo al gatear el render con `isLoading`: una
		// query apagada se queda en `status: 'loading'`, así que para eso va
		// `isInitialLoading`.
		enabled: computed(() => (params ? immediate : false) && !!resolvedId.value),
		retry: false,
		refetchOnWindowFocus: false,
	})

	const updateMutation = useMutation((params: UpdateParams) =>
		updateCompany(params, toast)
	)
	const createMutation = useMutation((params: CreateParams) =>
		createCompany(params, toast)
	)

	watch(
		data,
		(value) => {
			if (value) {
				setCompany(value)
			}
		},
		{ immediate: true, deep: true }
	)

	return {
		companyId: id,
		company,
		isError,
		isLoading,
		isInitialLoading,
		isSuccess,
		isFetching,
		isRefetching,
		data,

		refetch,
		setCompany,

		updateMutation,
		updateCompany: updateMutation.mutate,
		updateCompanyAsync: updateMutation.mutateAsync,
		updateCompanyLoading: updateMutation.isLoading,

		createMutation,
		createCompany: createMutation.mutate,
		createCompanyAsync: createMutation.mutateAsync,
		createCompanyLoading: createMutation.isLoading,
	}
}

export default useCompany
